/**
 * LocationPropsTab — Art Department "Location Props" view.
 * Groups all scenes by location. Each location card has:
 *  - Left panel: Claude AI prop suggestions (click + to add to bulk textarea)
 *  - Right panel: Bulk add textarea + existing props list for that location
 */
import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, Sparkles, Loader2, ChevronDown, ChevronRight, Trash2, MapPin, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { MLText } from './MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: 'PROP', label: 'Action Prop' },
  { value: 'ART_SET_DRESSING', label: 'Set Dressing' },
  { value: 'PICTURE_VEHICLE', label: 'Picture Vehicle' },
];

export function LocationPropsTab({ scenes, onRefresh }) {
  const { projectId } = useParams();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [expanded, setExpanded] = useState({});
  const [suggestions, setSuggestions] = useState({});   // locName → string[]
  const [loadingSug, setLoadingSug] = useState({});      // locName → bool
  const [bulkText, setBulkText] = useState({});          // locName → string
  const [bulkCat, setBulkCat] = useState({});            // locName → category string
  const [saving, setSaving] = useState({});              // locName → bool

  // Group scenes by location
  const locationGroups = useMemo(() => {
    const map = {};
    scenes.forEach(s => {
      const loc = (s.location_name || 'Unknown').trim();
      if (!map[loc]) map[loc] = { scenes: [], props: [] };
      map[loc].scenes.push(s);
      const sceneProps = [
        ...(s.breakdown_items?.PROP || []).map(p => ({ ...p, category: 'PROP', sceneNum: s.scene_number })),
        ...(s.breakdown_items?.ART_SET_DRESSING || []).map(p => ({ ...p, category: 'ART_SET_DRESSING', sceneNum: s.scene_number })),
        ...(s.breakdown_items?.PICTURE_VEHICLE || []).map(p => ({ ...p, category: 'PICTURE_VEHICLE', sceneNum: s.scene_number })),
      ];
      map[loc].props.push(...sceneProps);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [scenes]);

  const toggle = (loc) => setExpanded(p => ({ ...p, [loc]: !p[loc] }));

  const handleGetSuggestions = async (locName, locScenes) => {
    setLoadingSug(p => ({ ...p, [locName]: true }));
    try {
      const descriptions = locScenes
        .map(s => s.synopsis || s.scene_header || '')
        .filter(Boolean)
        .slice(0, 8);
      const res = await axios.post(
        `${API}/projects/${projectId}/art/location-props/suggestions`,
        { location_name: locName, scene_descriptions: descriptions },
        { headers }
      );
      setSuggestions(p => ({ ...p, [locName]: res.data.suggestions || [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestions failed');
    } finally {
      setLoadingSug(p => ({ ...p, [locName]: false }));
    }
  };

  const addSuggestionToText = (locName, suggestion) => {
    setBulkText(p => {
      const cur = p[locName] || '';
      return { ...p, [locName]: cur ? `${cur}\n${suggestion}` : suggestion };
    });
  };

  const handleBulkAdd = async (locName, sceneIds) => {
    const raw = bulkText[locName] || '';
    const descriptions = raw.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (!descriptions.length) { toast.error('Enter at least one prop'); return; }
    if (!sceneIds.length) { toast.error('No scenes for this location'); return; }
    setSaving(p => ({ ...p, [locName]: true }));
    try {
      await axios.post(
        `${API}/projects/${projectId}/breakdown-items/bulk`,
        { scene_ids: sceneIds, category: bulkCat[locName] || 'ART_SET_DRESSING', descriptions },
        { headers }
      );
      toast.success(`Added ${descriptions.length} props to ${sceneIds.length} scenes at ${locName}`);
      setBulkText(p => ({ ...p, [locName]: '' }));
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk add failed');
    } finally {
      setSaving(p => ({ ...p, [locName]: false }));
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/breakdown-items/${itemId}`, { headers });
      toast.success('Item removed');
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  if (!locationGroups.length) {
    return (
      <div className="text-center py-16 text-[#a0a0a0]">
        <MapPin className="h-12 w-12 mx-auto mb-3 text-[#2a2a2a]" />
        <p>No scenes found. Upload a script to see location props.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#a0a0a0]">
        Locations are derived from your script. Add props in bulk to all scenes at each location,
        or use AI suggestions to quickly populate your list.
      </p>
      {locationGroups.map(([locName, { scenes: locScenes, props: locProps }]) => {
        const sceneIds = locScenes.map(s => s.id);
        const isExpanded = expanded[locName];
        const sug = suggestions[locName] || [];
        const isLoading = loadingSug[locName] || false;
        const isSaving = saving[locName] || false;

        return (
          <div
            key={locName}
            className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden"
            data-testid={`loc-props-${locName.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {/* Accordion Header */}
            <button
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#222] transition-colors"
              onClick={() => toggle(locName)}
            >
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-[#4a9eff] shrink-0" />
                <span className="font-semibold text-[#f0f0f0]">{locName}</span>
                <div className="flex gap-1.5">
                  {locScenes.slice(0, 6).map(s => (
                    <span key={s.id} className="text-[10px] bg-[#e8c547]/20 text-[#e8c547] px-1.5 py-0.5 rounded font-mono">
                      {formatSceneNumber(s.scene_number, { prefix: '#' })}
                    </span>
                  ))}
                  {locScenes.length > 6 && (
                    <span className="text-[10px] text-[#a0a0a0]">+{locScenes.length - 6} more</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-xs">
                  <Layers className="h-3 w-3 mr-1" />{locProps.length} props
                </Badge>
                {isExpanded ? <ChevronDown className="h-4 w-4 text-[#a0a0a0]" /> : <ChevronRight className="h-4 w-4 text-[#a0a0a0]" />}
              </div>
            </button>

            {/* Expanded Body */}
            {isExpanded && (
              <div className="border-t border-[#2a2a2a] grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#2a2a2a]">

                {/* LEFT: AI Suggestions */}
                <div className="p-4 bg-[#0d0d0d]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest">AI Suggestions</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleGetSuggestions(locName, locScenes)}
                      disabled={isLoading}
                      className="h-6 px-2 text-[10px] border-[#e8c547]/40 text-[#e8c547] hover:bg-[#e8c547]/10"
                      data-testid={`ai-suggest-${locName}`}
                    >
                      {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                      {isLoading ? 'Thinking...' : (sug.length ? 'Refresh' : 'Generate')}
                    </Button>
                  </div>
                  {sug.length === 0 && !isLoading && (
                    <p className="text-xs text-[#555] italic">Click "Generate" to get AI-suggested props for this location.</p>
                  )}
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {sug.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between group px-2 py-1.5 rounded hover:bg-[#1a1a1a] transition-colors"
                        data-testid={`suggestion-item-${i}`}
                      >
                        <span className="text-xs text-[#d0d0d0] flex-1 mr-2">{s}</span>
                        <button
                          onClick={() => addSuggestionToText(locName, s)}
                          className="h-5 w-5 rounded bg-[#4a9eff]/20 text-[#4a9eff] hover:bg-[#4a9eff] hover:text-white flex items-center justify-center transition-colors shrink-0"
                          title="Add to bulk entry"
                          data-testid={`add-suggestion-${i}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT: Bulk Add + Existing Props */}
                <div className="p-4 col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Bulk Add */}
                    <div>
                      <div className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest mb-2">Bulk Add to All {locScenes.length} Scenes</div>
                      <Textarea
                        value={bulkText[locName] || ''}
                        onChange={e => setBulkText(p => ({ ...p, [locName]: e.target.value }))}
                        placeholder={`Props for ${locName}:\nOne per line, or comma-separated\nE.g., coffee mug, vintage radio, newspaper stack`}
                        className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] text-xs min-h-[120px] resize-none"
                        data-testid={`loc-bulk-textarea-${locName}`}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <Select
                          value={bulkCat[locName] || 'ART_SET_DRESSING'}
                          onValueChange={v => setBulkCat(p => ({ ...p, [locName]: v }))}
                        >
                          <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                            {CATEGORY_OPTIONS.map(c => (
                              <SelectItem key={c.value} value={c.value} className="text-[#f0f0f0] text-xs">{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => handleBulkAdd(locName, sceneIds)}
                          disabled={isSaving || !(bulkText[locName] || '').trim()}
                          className="bg-[#e8c547] hover:bg-[#d6b539] text-black h-7 text-xs whitespace-nowrap"
                          data-testid={`loc-bulk-add-btn-${locName}`}
                        >
                          {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                          Add Props
                        </Button>
                      </div>
                      <p className="text-[10px] text-[#555] mt-1">
                        {(bulkText[locName] || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean).length} prop(s) detected
                      </p>
                    </div>

                    {/* Existing Props */}
                    <div>
                      <div className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest mb-2">
                        Existing Props ({locProps.length})
                      </div>
                      {locProps.length === 0 ? (
                        <p className="text-xs text-[#444] italic">No props added yet.</p>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                          {locProps.map((item) => (
                            <div key={item.id} className="flex items-center justify-between group px-2 py-1.5 rounded hover:bg-[#1a1a1a] transition-colors">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <MLText as="span" className="text-xs text-[#f0f0f0] truncate flex-1">{getBreakdownItemLabel(item.description, 'original')}</MLText>
                                <span className="text-[9px] bg-[#2a2a2a] text-[#a0a0a0] px-1 rounded shrink-0">
                                  Sc.{item.sceneNum}
                                </span>
                                <span className={`text-[9px] px-1 rounded shrink-0 ${
                                  item.category === 'ART_SET_DRESSING' ? 'bg-amber-400/20 text-amber-400' :
                                  item.category === 'PICTURE_VEHICLE' ? 'bg-rose-400/20 text-rose-400' :
                                  'bg-purple-400/20 text-purple-400'
                                }`}>
                                  {item.category === 'ART_SET_DRESSING' ? 'Set' : item.category === 'PICTURE_VEHICLE' ? 'PV' : 'Prop'}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="h-5 w-5 rounded text-[#555] hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shrink-0"
                                data-testid={`delete-loc-prop-${item.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
